import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  status: string;
  className?: string;
}

// Word-Carries-State per DESIGN.md §10.9: full word in one of the 6 canonical
// colors, weight 600. No pill, no fill, no border, no dot, no glyph.
type Tone = "coral" | "forest" | "amber" | "primary" | "muted" | "red";

const TONE_CLASS: Record<Tone, string> = {
  coral: "text-[hsl(var(--comp-coral))]",
  forest: "text-[hsl(var(--forest-pulse))]",
  amber: "text-[hsl(var(--amber-state))]",
  primary: "text-[hsl(var(--ink-primary))]",
  muted: "text-[hsl(var(--ink-muted))]",
  red: "text-[hsl(var(--alert-red))]",
};

const STATUS_MAP: Record<string, { tone: Tone; label: string }> = {
  // forest — "thing went right" / current positive state
  active: { tone: "forest", label: "Ativa" },
  approved: { tone: "forest", label: "Aprovada" },
  accepted: { tone: "forest", label: "Aceita" },
  hired: { tone: "forest", label: "Contratado" },
  completed: { tone: "forest", label: "Concluída" },
  finished: { tone: "forest", label: "Finalizada" },
  meets: { tone: "forest", label: "Atende" },

  // coral — needs the operator's attention now
  pending_approval: { tone: "coral", label: "Aprovação pendente" },
  waiting_approval: { tone: "coral", label: "Aprovação pendente" },

  // amber — in motion, expected but not final
  in_progress: { tone: "amber", label: "Em andamento" },
  onboarding: { tone: "amber", label: "Onboarding" },
  has_active_instance: { tone: "amber", label: "Instância ativa" },
  screening: { tone: "amber", label: "Triagem" },
  interviewing: { tone: "amber", label: "Em entrevista" },
  reference_checks: { tone: "amber", label: "Checagem de referências" },
  dossier: { tone: "amber", label: "Comitê" },
  offer: { tone: "amber", label: "Oferta" },
  offer_sent: { tone: "amber", label: "Oferta enviada" },
  sent: { tone: "amber", label: "Enviada" },
  notified: { tone: "amber", label: "Notificado" },
  applied: { tone: "amber", label: "Inscrito" },
  prioritized: { tone: "amber", label: "Priorizado" },
  open: { tone: "amber", label: "Aberta" },
  pending: { tone: "amber", label: "Pendente" },
  scheduled: { tone: "amber", label: "Agendada" },
  awaiting_review: { tone: "amber", label: "Aguardando avaliação" },

  // muted — closed / declined / not relevant anymore
  draft: { tone: "muted", label: "Rascunho" },
  inactive: { tone: "muted", label: "Inativa" },
  closed: { tone: "muted", label: "Fechada" },
  cancelled: { tone: "muted", label: "Cancelada" },
  rejected: { tone: "muted", label: "Recusada" },
  declined: { tone: "muted", label: "Recusada pelo candidato" },
  deprioritized: { tone: "muted", label: "Despriorizado" },
  doesnt_meet: { tone: "muted", label: "Não atende" },
  no_active_instance: { tone: "muted", label: "Sem envio ativo" },
};

export function StatusBadge({ status, className }: StatusBadgeProps) {
  const entry = STATUS_MAP[status] ?? { tone: "primary" as Tone, label: status };
  return (
    <span
      data-testid={`status-${status}`}
      className={cn(
        "inline-flex items-center text-[13px] font-semibold whitespace-nowrap",
        TONE_CLASS[entry.tone],
        className,
      )}
    >
      {entry.label}
    </span>
  );
}
